// Temporary icon replacements until @mui/icons-material is properly installed
import React from 'react';

// These placeholders mimic the subset of the MUI SvgIcon props that call sites
// actually use (sx/fontSize/color/className/style/onClick/titleAccess) so they
// type-check as drop-in replacements. We forward what maps cleanly to a raw
// <svg> (size via fontSize, color, className, style, onClick) and ignore the
// MUI-only `sx` prop.
export interface TempIconProps {
  sx?: Record<string, unknown>;
  fontSize?: number | 'inherit' | 'small' | 'medium' | 'large' | string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  titleAccess?: string;
}

const FONT_SIZE_MAP: Record<string, number> = { small: 20, medium: 24, large: 35, inherit: 24 };

function svgPropsFrom({
  fontSize,
  color,
  className,
  style,
  onClick,
  titleAccess,
}: TempIconProps = {}): React.SVGProps<SVGSVGElement> {
  const size =
    typeof fontSize === 'number'
      ? fontSize
      : fontSize
        ? (FONT_SIZE_MAP[fontSize] ?? Number(fontSize)) || 24
        : 24;
  return {
    width: size,
    height: size,
    className,
    onClick,
    role: titleAccess ? 'img' : undefined,
    'aria-label': titleAccess,
    style: color ? { color, ...style } : style,
  };
}

// Simple SVG icon components as temporary replacements
export const SecurityIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.4 16,13V16C16,17.4 15.4,18 14.8,18H9.2C8.6,18 8,17.4 8,16V13C8,12.4 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z" />
  </svg>
);

export const ComplianceIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M16.84,2.73C16.45,2.73 16.07,2.88 15.77,3.17L13.65,5.29L18.95,10.6L21.07,8.5C21.67,7.89 21.67,6.94 21.07,6.36L17.9,3.17C17.6,2.88 17.22,2.73 16.84,2.73M12.94,6L4.84,14.11L7.4,16.67L15.5,8.56M4.25,15.04L2.5,21.73L9.2,19.94L4.25,15.04Z" />
  </svg>
);

export const AssessmentIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M9,17H7V10H9V17M13,17H11V7H13V17M17,17H15V13H17V17Z" />
  </svg>
);

export const WarningIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z" />
  </svg>
);

export const CheckCircleIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
  </svg>
);

export const ErrorIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M13,14H11V10H13M13,18H11V16H13M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z" />
  </svg>
);

export const ExpandMoreIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
  </svg>
);

export const ExpandLessIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
  </svg>
);

export const AIIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M17.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,9A1.5,1.5 0 0,1 19,10.5A1.5,1.5 0 0,1 17.5,12M10,12A1.5,1.5 0 0,1 8.5,10.5A1.5,1.5 0 0,1 10,9A1.5,1.5 0 0,1 11.5,10.5A1.5,1.5 0 0,1 10,12M12,14C9.33,14 7,15.33 7,17H17C17,15.33 14.67,14 12,14M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4Z" />
  </svg>
);

export const TimelineIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M23,8C23,9.1 22.1,10 21,10C20.82,10 20.65,9.96 20.5,9.9L19.13,11.27C19.35,11.85 19.35,12.15 19.13,12.73L20.5,14.1C20.65,14.04 20.82,14 21,14C22.1,14 23,14.9 23,16C23,17.1 22.1,18 21,18C19.9,18 19,17.1 19,16C19,15.82 19.04,15.65 19.1,15.5L17.73,14.13C17.15,14.35 16.85,14.35 16.27,14.13L14.9,15.5C14.96,15.65 15,15.82 15,16C15,17.1 14.1,18 13,18C11.9,18 11,17.1 11,16C11,14.9 11.9,14 13,14C13.18,14 13.35,14.04 13.5,14.1L14.87,12.73C14.65,12.15 14.65,11.85 14.87,11.27L13.5,9.9C13.35,9.96 13.18,10 13,10C11.9,10 11,9.1 11,8C11,6.9 11.9,6 13,6C14.1,6 15,6.9 15,8C15,8.18 14.96,8.35 14.9,8.5L16.27,9.87C16.85,9.65 17.15,9.65 17.73,9.87L19.1,8.5C19.04,8.35 19,8.18 19,8C19,6.9 19.9,6 21,6C22.1,6 23,6.9 23,8M6,12C7.1,12 8,12.9 8,14C8,15.1 7.1,16 6,16C4.9,16 4,15.1 4,14C4,12.9 4.9,12 6,12M6,20C7.1,20 8,20.9 8,22C8,23.1 7.1,24 6,24C4.9,24 4,23.1 4,22C4,20.9 4.9,20 6,20M6,4C7.1,4 8,4.9 8,6C8,7.1 7.1,8 6,8C4.9,8 4,7.1 4,6C4,4.9 4.9,4 6,4M6,8C7.1,8 8,8.9 8,10C8,11.1 7.1,12 6,12C4.9,12 4,11.1 4,10C4,8.9 4.9,8 6,8Z" />
  </svg>
);

export const ShieldIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1Z" />
  </svg>
);

export const ReviewIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M9,17H7V15H9V17M9,13H7V7H9V13M13,17H11V15H13V17M13,13H11V7H13V13M17,17H15V15H17V17M17,13H15V7H17V13Z" />
  </svg>
);

export const PerformanceIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.67 6.35,20.06C5.96,20.45 5.32,20.45 4.93,20.07V20.07C3.12,18.26 2,15.76 2,13A10,10 0 0,1 12,3M22,13C22,15.76 20.88,18.26 19.07,20.07V20.07C18.68,20.45 18.05,20.45 17.66,20.06C17.27,19.67 17.27,19.04 17.66,18.65H17.65C19.11,17.21 20,15.21 20,13C20,12 19.81,11 19.47,10.13L20.68,8.03C21.5,9.5 22,11.19 22,13Z" />
  </svg>
);

// Additional icons for other components
export const PlayArrowIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
  </svg>
);

export const PauseIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,19H18V5H14M6,19H10V5H6V19Z" />
  </svg>
);

export const ViewIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
  </svg>
);

// Agent-specific icons
export const LightbulbIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A7,7 0 0,0 5,9C5,11.38 6.19,13.47 8,14.74V17A1,1 0 0,0 9,18H15A1,1 0 0,0 16,17V14.74C17.81,13.47 19,11.38 19,9A7,7 0 0,0 12,2M9,21A1,1 0 0,0 10,22H14A1,1 0 0,0 15,21V20H9V21Z" />
  </svg>
);

export const ThinkingIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
  </svg>
);

export const DecisionIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2L2,7V17H6V13H18V17H22V7L12,2M12,4.84L18.54,8H5.46L12,4.84M4,19V21H20V19H4Z" />
  </svg>
);

export const ReflectionIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z" />
  </svg>
);

export const ToolIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M22.7,19L13.6,9.9C14.5,7.6 14,4.9 12.1,3C10.1,1 7.1,0.6 4.7,1.7L9,6L6,9L1.6,4.7C0.4,7.1 0.9,10.1 2.9,12.1C4.8,14 7.5,14.5 9.8,13.6L18.9,22.7C19.3,23.1 19.9,23.1 20.3,22.7L22.6,20.4C23.1,20 23.1,19.3 22.7,19Z" />
  </svg>
);

export const ToolsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M21.71,20.29L20.29,21.71A1,1 0 0,1 18.88,21.71L7,9.85A3.81,3.81 0 0,1 6,10A4,4 0 0,1 2,6A4,4 0 0,1 6,2A4,4 0 0,1 10,6C10,6.39 9.93,6.76 9.82,7.11L11.71,9C12.17,8.42 12.76,7.94 13.44,7.62L12.33,6.5L13.75,5.08L15.17,6.5L16.58,5.08L18,6.5L16.58,7.91L18,9.33L16.58,10.75L15.17,9.33L14.06,10.44C14.38,11.12 14.86,11.71 15.44,12.17L17.33,10.28C17.68,10.39 18.05,10.46 18.44,10.46A4,4 0 0,1 22.44,14.46A4,4 0 0,1 18.44,18.46C18.04,18.46 17.67,18.39 17.32,18.28L21.71,22.67A1,1 0 0,1 21.71,24.08L20.29,22.67L21.71,20.29M3.75,6.75A2.25,2.25 0 0,0 6,9A2.25,2.25 0 0,0 8.25,6.75A2.25,2.25 0 0,0 6,4.5A2.25,2.25 0 0,0 3.75,6.75M18.44,16.71A2.25,2.25 0 0,0 20.69,14.46A2.25,2.25 0 0,0 18.44,12.21A2.25,2.25 0 0,0 16.19,14.46A2.25,2.25 0 0,0 18.44,16.71Z" />
  </svg>
);

export const TimeIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
  </svg>
);

export const CostIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M7,15H9C9,16.08 10.37,17 12,17C13.63,17 15,16.08 15,15C15,13.9 13.96,13.5 11.76,12.97C9.64,12.44 7,11.78 7,9C7,7.21 8.47,5.69 10.5,5.18V3H13.5V5.18C15.53,5.69 17,7.21 17,9H15C15,7.92 13.63,7 12,7C10.37,7 9,7.92 9,9C9,10.1 10.04,10.5 12.24,11.03C14.36,11.56 17,12.22 17,15C17,16.79 15.53,18.31 13.5,18.82V21H10.5V18.82C8.47,18.31 7,16.79 7,15Z" />
  </svg>
);

export const ArtifactIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

export const TaskIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M3,5H9V11H3V5M5,7V9H7V7H5M11,7H21V9H11V7M11,15H21V17H11V15M5,20L1.5,16.5L2.91,15.09L5,17.17L9.59,12.59L11,14L5,20Z" />
  </svg>
);

export const StopIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M18,18H6V6H18V18Z" />
  </svg>
);

export const AddIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
  </svg>
);

export const DeleteIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
  </svg>
);

export const CloseIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
  </svg>
);

export const ApproveIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M23,10C23,8.89 22.1,8 21,8H14.68L15.64,3.43C15.66,3.33 15.67,3.22 15.67,3.11C15.67,2.7 15.5,2.32 15.23,2.05L14.17,1L7.59,7.58C7.22,7.95 7,8.45 7,9V19A2,2 0 0,0 9,21H18C18.83,21 19.54,20.5 19.84,19.78L22.86,12.73C22.95,12.5 23,12.26 23,12V10.08L23,10M1,21H5V9H1V21Z" />
  </svg>
);

export const RejectIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,15.18L20.5,16.68C20.5,16.69 20.5,16.69 20.5,16.7L22.39,18.59C22.78,18.98 22.78,19.61 22.39,20C22,20.39 21.37,20.39 20.98,20L19.09,18.11C19.08,18.11 19.08,18.11 19.08,18.1L17.58,16.6L19,15.18M15.54,5.54L13.76,4.22L12,6L10.24,4.22L8.46,5.54L10.24,7.78L8.46,10.02L10.24,11.34L12,9.56L13.76,11.34L15.54,10.02L13.76,7.78L15.54,5.54M1,21H5V9H1V21Z" />
  </svg>
);

export const CommentIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9Z" />
  </svg>
);

export const DownloadIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
  </svg>
);

export const MetricsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.67 6.35,20.06C5.96,20.45 5.32,20.45 4.93,20.07V20.07C3.12,18.26 2,15.76 2,13A10,10 0 0,1 12,3M22,13C22,15.76 20.88,18.26 19.07,20.07V20.07C18.68,20.45 18.05,20.45 17.66,20.06C17.27,19.67 17.27,19.04 17.66,18.65H17.65C19.11,17.21 20,15.21 20,13C20,12 19.81,11 19.47,10.13L20.68,8.03C21.5,9.5 22,11.19 22,13Z" />
  </svg>
);

// Additional icons for UnifiedWorkflow
export const ScheduleIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
  </svg>
);

export const ArchitectureIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,3L2,12H5V20H19V12H22L12,3M12,8.75A1.25,1.25 0 0,1 13.25,10A1.25,1.25 0 0,1 12,11.25A1.25,1.25 0 0,1 10.75,10A1.25,1.25 0 0,1 12,8.75M12,6.5A3.5,3.5 0 0,0 8.5,10A3.5,3.5 0 0,0 12,13.5A3.5,3.5 0 0,0 15.5,10A3.5,3.5 0 0,0 12,6.5Z" />
  </svg>
);

export const CodeIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14.6,16.6L19.2,12L14.6,7.4L16,6L22,12L16,18L14.6,16.6M9.4,16.6L4.8,12L9.4,7.4L8,6L2,12L8,18L9.4,16.6Z" />
  </svg>
);

export const DeployIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

// Icons for RPGUploadModal
export const UploadIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

export const FileIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

// Icons for AppSidebar
export const DashboardIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M13,3V9H21V3M13,21H21V11H13M3,21H11V15H3M3,13H11V3H3V13Z" />
  </svg>
);

export const WorkspacesIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z" />
  </svg>
);

export const AnalyticsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z" />
  </svg>
);

export const AgentDevIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A4,4 0 0,1 16,6V8H18A2,2 0 0,1 20,10V18A2,2 0 0,1 18,20H6A2,2 0 0,1 4,18V10A2,2 0 0,1 6,8H8V6A4,4 0 0,1 12,2M10,8H14V6A2,2 0 0,0 12,4A2,2 0 0,0 10,6V8M8.5,13A1.5,1.5 0 0,0 7,14.5A1.5,1.5 0 0,0 8.5,16A1.5,1.5 0 0,0 10,14.5A1.5,1.5 0 0,0 8.5,13M15.5,13A1.5,1.5 0 0,0 14,14.5A1.5,1.5 0 0,0 15.5,16A1.5,1.5 0 0,0 17,14.5A1.5,1.5 0 0,0 15.5,13Z" />
  </svg>
);

export const TemplatesIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

export const SpecificationsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14.6,16.6L19.2,12L14.6,7.4L16,6L22,12L16,18L14.6,16.6M9.4,16.6L4.8,12L9.4,7.4L8,6L2,12L8,18L9.4,16.6Z" />
  </svg>
);

export const IntegrationsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20.5,11H19V7C19,5.89 18.1,5 17,5H13V3.5A2.5,2.5 0 0,0 10.5,1A2.5,2.5 0 0,0 8,3.5V5H4A2,2 0 0,0 2,7V10.8H3.5C5,10.8 6.2,12 6.2,13.5C6.2,15 5,16.2 3.5,16.2H2V20A2,2 0 0,0 4,22H7.8V20.5C7.8,19 9,17.8 10.5,17.8C12,17.8 13.2,19 13.2,20.5V22H17A2,2 0 0,0 19,20V16H20.5A2.5,2.5 0 0,0 23,13.5A2.5,2.5 0 0,0 20.5,11Z" />
  </svg>
);

export const KnowledgeIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,2L14,6.5V17.5L19,13V2M6.5,5C4.55,5 2.45,5.4 1,6.5V21.16C1,21.41 1.25,21.66 1.5,21.66C1.6,21.66 1.65,21.59 1.75,21.59C3.1,20.94 5.05,20.68 6.5,20.68C8.45,20.68 10.55,21.1 12,22C13.35,21.15 15.8,20.68 17.5,20.68C19.15,20.68 20.85,21.1 22.25,21.56C22.35,21.61 22.4,21.59 22.5,21.59C22.75,21.59 23,21.34 23,21.09V6.5C22.4,6.05 21.75,5.75 21,5.5V19.6C19.9,19.2 18.7,19 17.5,19C15.8,19 14.35,19.4 13,20.2V8.5C14.35,7.65 15.8,7.2 17.5,7.2C18.7,7.2 19.9,7.4 21,7.8V6.5C20.05,6.05 18.95,5.75 17.5,5.75C15.8,5.75 14.35,6.15 13,7V19.15C12.2,18.65 10.8,18.2 9.5,18.2C8.2,18.2 6.8,18.65 6,19.15V7C4.55,6.05 3.1,5.55 1.75,5.25C2.45,5.4 4.55,5 6.5,5Z" />
  </svg>
);

export const SettingsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
  </svg>
);

// Icons for GitHub and other components
export const GitHubIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A10,10 0 0,0 2,12C2,16.42 4.87,20.17 8.84,21.5C9.34,21.58 9.5,21.27 9.5,21C9.5,20.77 9.5,20.14 9.5,19.31C6.73,19.91 6.14,17.97 6.14,17.97C5.68,16.81 5.03,16.5 5.03,16.5C4.12,15.88 5.1,15.9 5.1,15.9C6.1,15.97 6.63,16.93 6.63,16.93C7.5,18.45 8.97,18 9.54,17.76C9.63,17.11 9.89,16.67 10.17,16.42C7.95,16.17 5.62,15.31 5.62,11.5C5.62,10.39 6,9.5 6.65,8.79C6.55,8.54 6.2,7.5 6.75,6.15C6.75,6.15 7.59,5.88 9.5,7.17C10.29,6.95 11.15,6.84 12,6.84C12.85,6.84 13.71,6.95 14.5,7.17C16.41,5.88 17.25,6.15 17.25,6.15C17.8,7.5 17.45,8.54 17.35,8.79C18,9.5 18.38,10.39 18.38,11.5C18.38,15.32 16.04,16.16 13.81,16.41C14.17,16.72 14.5,17.33 14.5,18.26C14.5,19.6 14.5,20.68 14.5,21C14.5,21.27 14.66,21.59 15.17,21.5C19.14,20.16 22,16.42 22,12A10,10 0 0,0 12,2Z" />
  </svg>
);

export const FolderIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z" />
  </svg>
);

// Icons for ModernLoginPage
export const MicrosoftIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M11.4,24H0V12.6H11.4V24M24,24H12.6V12.6H24V24M11.4,11.4H0V0H11.4V11.4M24,11.4H12.6V0H24V11.4Z" />
  </svg>
);

export const DarkModeIcon: React.FC<TempIconProps> = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M17.75,4.09L15.22,6.03L16.13,9.09L13.5,7.28L10.87,9.09L11.78,6.03L9.25,4.09L12.44,4L13.5,1L14.56,4L17.75,4.09M21.25,11L19.61,12.25L20.2,14.23L18.5,13.06L16.8,14.23L17.39,12.25L15.75,11L17.81,10.95L18.5,9L19.19,10.95L21.25,11M18.97,15.95C19.8,15.87 20.69,17.05 20.16,17.8C19.84,18.25 19.5,18.67 19.08,19.07C15.17,23 8.84,23 4.94,19.07C1.03,15.17 1.03,8.83 4.94,4.93C5.34,4.53 5.76,4.17 6.21,3.85C6.96,3.32 8.14,4.21 8.06,5.04C7.79,7.9 8.75,10.87 10.95,13.06C13.14,15.26 16.1,16.22 18.97,15.95M17.33,17.97C14.5,17.81 11.7,16.64 9.53,14.5C7.36,12.31 6.2,9.5 6.04,6.68C3.23,9.82 3.34,14.4 6.35,17.41C9.37,20.43 14,20.54 17.33,17.97Z" />
  </svg>
);

export const LightModeIcon: React.FC<TempIconProps> = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8M12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18M20,8.69V4H15.31L12,0.69L8.69,4H4V8.69L0.69,12L4,15.31V20H8.69L12,23.31L15.31,20H20V15.31L23.31,12L20,8.69Z" />
  </svg>
);

export const BusinessIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,7V3H2V21H22V7H12M6,19H4V17H6V19M6,15H4V13H6V15M6,11H4V9H6V11M6,7H4V5H6V7M10,19H8V17H10V19M10,15H8V13H10V15M10,11H8V9H10V11M10,7H8V5H10V7M20,19H12V17H14V15H12V13H14V11H12V9H20V19M18,11H16V13H18V11M18,15H16V17H18V15Z" />
  </svg>
);

export const TransformIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A2,2 0 0,1 14,4C14,4.74 13.6,5.39 13,5.73V7.27L21,15.27V16.73C21.6,17.07 22,17.72 22,18.5A2.5,2.5 0 0,1 19.5,21A2.5,2.5 0 0,1 17,18.5C17,17.72 17.4,17.07 18,16.73V15.27L10,7.27V5.73C9.4,5.39 9,4.74 9,4A2,2 0 0,1 12,2M7,9A2,2 0 0,1 9,11C9,11.74 8.6,12.39 8,12.73V13.27L13,18.27V19.73C13.6,20.07 14,20.72 14,21.5A2.5,2.5 0 0,1 11.5,24A2.5,2.5 0 0,1 9,21.5C9,20.72 9.4,20.07 10,19.73V18.27L5,13.27V12.73C4.4,12.39 4,11.74 4,11A2,2 0 0,1 7,9Z" />
  </svg>
);

// Additional icons for GitHub components
export const SearchIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
  </svg>
);

export const LinkIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z" />
  </svg>
);

// Icons for StorybookDashboard
export const BookIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,2L14,6.5V17.5L19,13V2M6.5,5C4.55,5 2.45,5.4 1,6.5V21.16C1,21.41 1.25,21.66 1.5,21.66C1.6,21.66 1.65,21.59 1.75,21.59C3.1,20.94 5.05,20.68 6.5,20.68C8.45,20.68 10.55,21.1 12,22C13.35,21.15 15.8,20.68 17.5,20.68C19.15,20.68 20.85,21.1 22.25,21.56C22.35,21.61 22.4,21.59 22.5,21.59C22.75,21.59 23,21.34 23,21.09V6.5C22.4,6.05 21.75,5.75 21,5.5V19.6C19.9,19.2 18.7,19 17.5,19C15.8,19 14.35,19.4 13,20.2V8.5C14.35,7.65 15.8,7.2 17.5,7.2C18.7,7.2 19.9,7.4 21,7.8V6.5C20.05,6.05 18.95,5.75 17.5,5.75C15.8,5.75 14.35,6.15 13,7V19.15C12.2,18.65 10.8,18.2 9.5,18.2C8.2,18.2 6.8,18.65 6,19.15V7C4.55,6.05 3.1,5.55 1.75,5.25C2.45,5.4 4.55,5 6.5,5Z" />
  </svg>
);

export const PlayIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
  </svg>
);

export const CompleteIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
  </svg>
);

export const PendingIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
  </svg>
);

export const BlockedIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M13,14H11V10H13M13,18H11V16H13M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z" />
  </svg>
);

export const PersonIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
  </svg>
);

export const AgentIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M17.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,9A1.5,1.5 0 0,1 19,10.5A1.5,1.5 0 0,1 17.5,12M10,12A1.5,1.5 0 0,1 8.5,10.5A1.5,1.5 0 0,1 10,9A1.5,1.5 0 0,1 11.5,10.5A1.5,1.5 0 0,1 10,12M12,14C9.33,14 7,15.33 7,17H17C17,15.33 14.67,14 12,14M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4Z" />
  </svg>
);

// Icons for Integrations page
export const ExtensionIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20.5,11H19V7C19,5.89 18.1,5 17,5H13V3.5A2.5,2.5 0 0,0 10.5,1A2.5,2.5 0 0,0 8,3.5V5H4A2,2 0 0,0 2,7V10.8H3.5C5,10.8 6.2,12 6.2,13.5C6.2,15 5,16.2 3.5,16.2H2V20A2,2 0 0,0 4,22H7.8V20.5C7.8,19 9,17.8 10.5,17.8C12,17.8 13.2,19 13.2,20.5V22H17A2,2 0 0,0 19,20V16H20.5A2.5,2.5 0 0,0 23,13.5A2.5,2.5 0 0,0 20.5,11Z" />
  </svg>
);

export const CloudSyncIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M21.5,14.98C20.84,14.7 20.14,14.55 19.43,14.55C16.53,14.55 14.13,16.61 13.65,19.5H6.5A2.5,2.5 0 0,1 4,17C4,15.62 4.9,14.5 6.14,14.05C6.05,13.54 6,12.79 6,12.5C6,9.46 8.46,7 11.5,7C14.54,7 17,9.46 17,12.5V13H19.43C19.93,13 20.42,13.08 20.88,13.23C21.24,12.04 22,10.79 22,9.5C22,7.29 20.21,5.5 18,5.5C16.81,5.5 15.73,6.08 15.07,7.09C14.42,6.42 13.54,6 12.56,6C10.93,6 9.58,7.35 9.58,9C9.58,9.34 9.65,9.67 9.77,9.97C9.17,10.25 8.69,10.72 8.4,11.31C7.84,11.11 7.18,11 6.5,11A3.5,3.5 0 0,0 3,14.5C3,16.43 4.57,18 6.5,18H13.65C14.13,15.11 16.53,13.05 19.43,13.05C20.14,13.05 20.84,13.2 21.5,13.48V14.98M19,18V16L16,19L19,22V20C20.68,20 22,18.68 22,17C22,16.65 21.92,16.31 21.78,16H20.5C20.63,16.31 20.7,16.65 20.7,17C20.7,17.96 19.96,18.7 19,18.7V18Z" />
  </svg>
);

export const ApiIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M7,7H17V10H7V7M7,13H17V16H7V13M7,19H17V22H7V19M3,7H5V10H3V7M3,13H5V16H3V13M3,19H5V22H3V19Z" />
  </svg>
);

export const DatabaseIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,3C7.58,3 4,4.79 4,7C4,9.21 7.58,11 12,11C16.42,11 20,9.21 20,7C20,4.79 16.42,3 12,3M4,9V12C4,14.21 7.58,16 12,16C16.42,16 20,14.21 20,12V9C20,11.21 16.42,13 12,13C7.58,13 4,11.21 4,9M4,14V17C4,19.21 7.58,21 12,21C16.42,21 20,19.21 20,17V14C20,16.21 16.42,18 12,18C7.58,18 4,16.21 4,14Z" />
  </svg>
);

// Icons for AppHeader
export const LogoutIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z" />
  </svg>
);

// Icons for Analytics page
export const TrendingUpIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z" />
  </svg>
);

export const PieChartIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M11,2V22C5.9,21.5 2,17.2 2,12C2,6.8 5.9,2.5 11,2M13,2V11H22C22,6.8 18.1,2.5 13,2M13,13V22C18.1,21.5 22,17.2 22,12H13Z" />
  </svg>
);

// Icons for StorybookDetail page
export const ArrowBackIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
  </svg>
);

export const AnalysisIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M7,2V4H8V18A4,4 0 0,0 12,22A4,4 0 0,0 16,18V4H17V2H7M11,16C10.4,16 10,15.6 10,15C10,14.4 10.4,14 11,14C11.6,14 12,14.4 12,15C12,15.6 11.6,16 11,16M13,12C12.4,12 12,11.6 12,11C12,10.4 12.4,10 13,10C13.6,10 14,10.4 14,11C14,11.6 13.6,12 13,12M14,7H10V4H14V7Z" />
  </svg>
);

export const RequirementsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

export const BuildIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M22.7,19L13.6,9.9C14.5,7.6 14,4.9 12.1,3C10.1,1 7.1,0.6 4.7,1.7L9,6L6,9L1.6,4.7C0.4,7.1 0.9,10.1 2.9,12.1C4.8,14 7.5,14.5 9.8,13.6L18.9,22.7C19.3,23.1 19.9,23.1 20.3,22.7L22.6,20.4C23.1,20 23.1,19.3 22.7,19Z" />
  </svg>
);

export const CompletedIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
  </svg>
);

export const SyncIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,18A6,6 0 0,1 6,12C6,11 6.25,10.03 6.7,9.2L5.24,7.74C4.46,8.97 4,10.43 4,12A8,8 0 0,0 12,20V23L16,19L12,15M12,4V1L8,5L12,9V6A6,6 0 0,1 18,12C18,13 17.75,13.97 17.3,14.8L18.76,16.26C19.54,15.03 20,13.57 20,12A8,8 0 0,0 12,4Z" />
  </svg>
);

// Icons for remaining pages - Dashboard, Projects, Workspaces
export const CalendarIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19Z" />
  </svg>
);

export const EditIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
  </svg>
);

export const MoreVertIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" />
  </svg>
);

export const CloudUploadIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

export const GroupIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M16,4C18.21,4 20,5.79 20,8C20,10.21 18.21,12 16,12C13.79,12 12,10.21 12,8C12,5.79 13.79,4 16,4M16,14C18.67,14 24,15.33 24,18V20H8V18C8,15.33 13.33,14 16,14M8.5,4C10.71,4 12.5,5.79 12.5,8C12.5,10.21 10.71,12 8.5,12C6.29,12 4.5,10.21 4.5,8C4.5,5.79 6.29,4 8.5,4M8.5,14C11.17,14 16.5,15.33 16.5,18V20H0.5V18C0.5,15.33 5.83,14 8.5,14Z" />
  </svg>
);

export const ProgressIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z" />
  </svg>
);

export const ArchiveIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M3,3H21V7H3V3M4,8H20V21H4V8M9.5,11A0.5,0.5 0 0,0 9,11.5V13H15V11.5A0.5,0.5 0 0,0 14.5,11H9.5Z" />
  </svg>
);

// Icons for LiquidGlassDemo
export const LayersIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,16L19.36,10.27L21,9L12,2L3,9L4.63,10.27M12,18.54L4.62,12.81L3,14.07L12,21.07L21,14.07L19.38,12.8L12,18.54Z" />
  </svg>
);

export const TuneIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M3,17V19H9V17H3M3,5V7H13V5H3M13,21V19H21V17H13V15H11V21H13M7,9V11H3V13H7V15H9V9H7M21,13V11H11V13H21M15,9H17V7H21V5H17V3H15V9Z" />
  </svg>
);

export const VisibilityIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
  </svg>
);

export const TouchIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M9,11H7L13,5L19,11H17V20H9V11M12,6.8L9.8,9H10V18H16V9H16.2L12,6.8Z" />
  </svg>
);

// Icons for FileProcessingProgress
export const ProcessingIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
  </svg>
);

export const CollapseIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
  </svg>
);

export const SpeedIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.67 6.35,20.06C5.96,20.45 5.32,20.45 4.93,20.07V20.07C3.12,18.26 2,15.76 2,13A10,10 0 0,1 12,3M22,13C22,15.76 20.88,18.26 19.07,20.07V20.07C18.68,20.45 18.05,20.45 17.66,20.06C17.27,19.67 17.27,19.04 17.66,18.65H17.65C19.11,17.21 20,15.21 20,13C20,12 19.81,11 19.47,10.13L20.68,8.03C21.5,9.5 22,11.19 22,13Z" />
  </svg>
);

// Icons for RepositoryTreeView
export const FolderOpenIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,20H4C2.89,20 2,19.1 2,18V6C2,4.89 2.89,4 4,4H10L12,6H19A2,2 0 0,1 21,8H21L4,8V18L6.14,10H23.21L20.93,18.5C20.7,19.37 19.92,20 19,20Z" />
  </svg>
);

export const InfoIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
  </svg>
);

// Icons for StoryboardView
export const IntegrationIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20.5,11H19V7C19,5.89 18.1,5 17,5H13V3.5A2.5,2.5 0 0,0 10.5,1A2.5,2.5 0 0,0 8,3.5V5H4A2,2 0 0,0 2,7V10.8H3.5C5,10.8 6.2,12 6.2,13.5C6.2,15 5,16.2 3.5,16.2H2V20A2,2 0 0,0 4,22H7.8V20.5C7.8,19 9,17.8 10.5,17.8C12,17.8 13.2,19 13.2,20.5V22H17A2,2 0 0,0 19,20V16H20.5A2.5,2.5 0 0,0 23,13.5A2.5,2.5 0 0,0 20.5,11Z" />
  </svg>
);

export const MilestoneIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M14,8H22L20,12L22,16H14V19H12V16H2V14L6,8H12V5H14V8M14,10V14H18.5L17.5,12L18.5,10H14M12,10H7.5L5,14H12V10Z" />
  </svg>
);

export const RateReviewIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20,2H4A2,2 0 0,0 2,4V16A2,2 0 0,0 4,18H18L22,22V4A2,2 0 0,0 20,2M6,9H18V11H6M6,6H18V8H6M6,12H16V14H6" />
  </svg>
);

// Icons for AISceneExecution
export const RefreshIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
  </svg>
);

export const ReviewerIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
  </svg>
)

// Icons for LandingPage
export const AutoAwesomeIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19,9L17.74,6.26L15,5L17.74,3.74L19,1L20.25,3.74L23,5L20.25,6.26M11,9L9,4L7,9L2,11L7,13L9,18L11,13L16,11M19,15L17.74,17.74L15,19L17.74,20.25L19,23L20.25,20.25L23,19L20.25,17.74" />
  </svg>
)

export const CloudIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M19.35,10.04C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.04C2.34,8.36 0,10.91 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.04Z" />
  </svg>
)

export const IntegrationInstructionsIcon = (props: TempIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...svgPropsFrom(props)}>
    <path d="M20.5,11H19V7C19,5.89 18.1,5 17,5H13V3.5A2.5,2.5 0 0,0 10.5,1A2.5,2.5 0 0,0 8,3.5V5H4A2,2 0 0,0 2,7V10.8H3.5C5,10.8 6.2,12 6.2,13.5C6.2,15 5,16.2 3.5,16.2H2V20A2,2 0 0,0 4,22H7.8V20.5C7.8,19 9,17.8 10.5,17.8C12,17.8 13.2,19 13.2,20.5V22H17A2,2 0 0,0 19,20V16H20.5A2.5,2.5 0 0,0 23,13.5A2.5,2.5 0 0,0 20.5,11Z" />
  </svg>
);
